import { assertNonNullable, assertUnreachable, requireNonNullable } from '@disy/lib/custom-error';
import { logger } from '@disy/lib/logging';
import { keys } from '@disy/lib/type-util';
import { getSetting } from '@disy/workbook.lib/settings';

import { visiblePerPreconditions } from 'cadenza/api-client/workbook-api';
import { Command } from 'cadenza/api-client/workbook-commands';
import type { AttributeStatus } from 'cadenza/repository/model/attribute-status.gen';
import { type AttributeType, isNumeric } from 'cadenza/repository/model/attribute-type.gen';
import { MeasureAggregation, getTargetAttributeType } from 'cadenza/repository/model/measure-aggregation.gen';
import { isValidMainObjecttype } from 'cadenza/repository/model/objecttype-id.gen';
import { getStore } from 'cadenza/store/cadenza-store';
import { parseNumber } from 'cadenza/utils/number-util';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import {
  type ChartDataViewVisualizationKey,
  DataViewVisualizationKey,
  asChartDataViewVisualizationKey,
  isViewType,
} from 'cadenza/workbook/api/data-view-visualization-key';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { ViewType } from 'cadenza/workbook/api/view-type';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AttributeHighlightingRule } from 'cadenza/workbook/attribute-modification-dialog/presentation/attribute-highlighting/attribute-highlighting-types';
import type { DataView } from 'cadenza/workbook/data-view/data-view';
import type {
  AttributeBundleDefinitionDto,
  AttributeBundleDefinitions,
} from 'cadenza/workbook/model/attribute-bundle-definition-dto';
import type { AttributesByBundle, ByBundle } from 'cadenza/workbook/model/attribute-bundle-name';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type { WorkbookNotificationId } from 'cadenza/workbook/notifications/workbook-notifications';
import { createNotification } from 'cadenza/workbook/notifications/workbook-notifications';
import { getDisplayRuleHintText } from 'cadenza/workbook/rule/rule-description';
import { getAcceptedAttributeIdsByDataViewId } from 'cadenza/workbook/state/workbook-reachability.state';
import { getAllowedAggregations } from 'cadenza/workbook/workbook-attribute-util';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  getActiveView,
  getAttributeIdsFromBundle,
  getAttributes,
  getAttributesByBundle,
  getDataViewById,
  getDataViewIdsByViewId,
  getDataViewStatus,
  getDataViewsByViewId,
  getViewTypeByDataViewId,
  getVisualizationKeyByDataViewId,
  getWorkbookNameAndHash,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { createDataViewAttributeModificationCommand } from 'cadenza/workbook/workbook-panel/settings/attribute-modification-utils';
import { addNotifications, removeNotifications } from 'cadenza/workbook/workbook.state';

const PRECONDITION_UNFULFILLED_NOTIFICATION_ID = 'precondition-unfulfilled-notification-id' as WorkbookNotificationId;

/** Attribute count and limits for one attribute bundle */
export interface BundleAttributeCountAndLimits {
  /** The current number of attributes defined in the bundle*/
  current: number;
  /** The minimum number of attributes required in the bundle*/
  min: number;
  /** The maximum number of attributes allowed in the bundle*/
  max: number;
}

/**
 * @see net/disy/cadenza/workbook/configuration/AttributeBundleDefinitionLimitsSpecifics.java
 */
export interface AttributeBundleDefinitionLimitsSpecifics {
  olapTableMaxRowDimensionsLimit: number;
  olapTableMaxMeasuresLimit: number;
  pivotTableMaxRowDimensionsLimit: number;
  pivotTableMaxColumnDimensionsLimit: number;
  pivotTableMaxMeasuresLimit: number;
  defaultMaxMeasuresLimit: number;
}

export function isColumnAttribute(attribute: WorkbookAttributeDto, dataViewId: DataViewId) {
  if (isPivotTable(dataViewId)) {
    const columnAttributes = getAttributeIdsFromBundle(getStore().getState(), dataViewId, 'COLUMNS');
    return columnAttributes.some((attributeId) => attributeId === attribute.id);
  }
}

export function isPivotTable(dataViewId: DataViewId) {
  return isTable(dataViewId) && getAttributeIdsFromBundle(getStore().getState(), dataViewId, 'COLUMNS').length > 0;
}

export function isTable(dataViewId: DataViewId) {
  return isViewType(getVisualizationKeyByDataViewId(getStore().getState(), dataViewId), ViewType.TABLE);
}

export function isIndicator(dataViewId: DataViewId) {
  return getVisualizationKeyByDataViewId(getStore().getState(), dataViewId) === 'OTHER.INDICATOR';
}

// Chart that doesn't have a dimension attribute but multiple measures instead
export function isMultiMeasureSimpleChart(dataViewId: DataViewId) {
  const { MULTI_MEASURE_COLUMN_CHART, MULTI_MEASURE_BAR_CHART, MULTI_MEASURE_LINE_CHART } =
    DataViewVisualizationKey[ViewType.CHART];
  const chartTypes = [MULTI_MEASURE_COLUMN_CHART, MULTI_MEASURE_BAR_CHART, MULTI_MEASURE_LINE_CHART];
  return chartTypes.includes(getVisualizationKeyByDataViewId(getStore().getState(), dataViewId));
}

export function isSimpleChart(dataViewId: DataViewId) {
  const { COLUMN_CHART, BAR_CHART, LINE_CHART } = DataViewVisualizationKey[ViewType.CHART];
  const chartTypes = [COLUMN_CHART, BAR_CHART, LINE_CHART];
  return chartTypes.includes(getVisualizationKeyByDataViewId(getStore().getState(), dataViewId));
}

export function isMeasureMirrorBarChart(dataViewId: DataViewId) {
  const visualizationKey = getVisualizationKeyByDataViewId(getStore().getState(), dataViewId);

  if (
    visualizationKey !== DataViewVisualizationKey.CHART.MIRROR_BAR_CHART &&
    visualizationKey !== DataViewVisualizationKey.CHART.MULTI_MEASURE_MIRROR_BAR_CHART
  ) {
    return false;
  }

  const allAttributeBundles = getAttributesByBundle(getStore().getState(), dataViewId);

  return allAttributeBundles?.['DIMENSION']?.length === 1 && allAttributeBundles['MEASURE']?.length === 2;
}

export function getGeometryAttributeActiveId(dataViewId: DataViewId): ActiveAttributeId | undefined {
  const dataView = getDataViewById(getStore().getState(), dataViewId);
  return dataView.attributes['GEOMETRY']?.[0];
}

export function getChartCategoryAttributes(dataViewId: DataViewId) {
  const visualization = getVisualizationKeyByDataViewId(getStore().getState(), dataViewId);
  const attributesByBundle = getAttributesByBundle(getStore().getState(), dataViewId);
  // In multi-measure 'DIMENSION' bundle is not present, and also in general the bundle can have zero attribute.
  // In such case, undefined is returned.
  const bundleName = getCategoryAttributeBundle(asChartDataViewVisualizationKey(visualization));
  return (bundleName && attributesByBundle[bundleName]) ?? [];
}

export function getChartValueAttributes(dataViewId: DataViewId) {
  const visualization = getVisualizationKeyByDataViewId(getStore().getState(), dataViewId);
  const attributesByBundle = getAttributesByBundle(getStore().getState(), dataViewId);
  const valueAttributeBundle = getValueAttributeBundle(asChartDataViewVisualizationKey(visualization));
  return (valueAttributeBundle && attributesByBundle[valueAttributeBundle]) ?? [];
}

function getCategoryAttributeBundle(chartVisualizationKey: ChartDataViewVisualizationKey) {
  switch (chartVisualizationKey) {
    case 'CHART.BULLET_BAR_CHART':
    case 'CHART.BULLET_COLUMN_CHART':
    case 'CHART.COLUMN_CHART':
    case 'CHART.GROUPED_COLUMN_CHART':
    case 'CHART.STACKED_COLUMN_CHART':
    case 'CHART.HUNDRED_PERCENT_COLUMN_CHART':
    case 'CHART.BAR_CHART':
    case 'CHART.GROUPED_BAR_CHART':
    case 'CHART.STACKED_BAR_CHART':
    case 'CHART.HUNDRED_PERCENT_BAR_CHART':
    case 'CHART.MIRROR_BAR_CHART':
    case 'CHART.PIE_CHART':
    case 'CHART.LINE_CHART':
    case 'CHART.MULTI_LINE_CHART':
    case 'CHART.STACKED_AREA_CHART':
    case 'CHART.MULTI_MEASURE_GROUPED_COLUMN_CHART':
    case 'CHART.MULTI_MEASURE_STACKED_COLUMN_CHART':
    case 'CHART.MULTI_MEASURE_HUNDRED_PERCENT_COLUMN_CHART':
    case 'CHART.MULTI_MEASURE_GROUPED_BAR_CHART':
    case 'CHART.MULTI_MEASURE_STACKED_BAR_CHART':
    case 'CHART.MULTI_MEASURE_HUNDRED_PERCENT_BAR_CHART':
    case 'CHART.MULTI_MEASURE_MIRROR_BAR_CHART':
    case 'CHART.MULTI_MEASURE_MULTI_LINE_CHART':
    case 'CHART.MULTI_MEASURE_STACKED_AREA_CHART':
      return 'DIMENSION';
    case 'CHART.SCATTER_PLOT_CHART':
      return 'SCATTER_X';
    case 'CHART.MULTI_MEASURE_COLUMN_CHART':
    case 'CHART.MULTI_MEASURE_BAR_CHART':
    case 'CHART.MULTI_MEASURE_LINE_CHART':
    case 'CHART.MULTI_MEASURE_PIE_CHART':
    case 'CHART.REFERENCE_LINE_CHART':
    case 'CHART.STATIC_REFERENCE_LINE_CHART':
    case 'CHART.TREND_LINE_CHART':
      return undefined;
  }

  assertUnreachable(chartVisualizationKey);
}

function getValueAttributeBundle(chartVisualizationKey: ChartDataViewVisualizationKey) {
  switch (chartVisualizationKey) {
    case 'CHART.BULLET_BAR_CHART':
    case 'CHART.BULLET_COLUMN_CHART':
    case 'CHART.COLUMN_CHART':
    case 'CHART.GROUPED_COLUMN_CHART':
    case 'CHART.STACKED_COLUMN_CHART':
    case 'CHART.HUNDRED_PERCENT_COLUMN_CHART':
    case 'CHART.BAR_CHART':
    case 'CHART.GROUPED_BAR_CHART':
    case 'CHART.STACKED_BAR_CHART':
    case 'CHART.HUNDRED_PERCENT_BAR_CHART':
    case 'CHART.MIRROR_BAR_CHART':
    case 'CHART.PIE_CHART':
    case 'CHART.LINE_CHART':
    case 'CHART.MULTI_LINE_CHART':
    case 'CHART.STACKED_AREA_CHART':
    case 'CHART.MULTI_MEASURE_COLUMN_CHART':
    case 'CHART.MULTI_MEASURE_BAR_CHART':
    case 'CHART.MULTI_MEASURE_LINE_CHART':
    case 'CHART.MULTI_MEASURE_PIE_CHART':
    case 'CHART.MULTI_MEASURE_GROUPED_COLUMN_CHART':
    case 'CHART.MULTI_MEASURE_STACKED_COLUMN_CHART':
    case 'CHART.MULTI_MEASURE_HUNDRED_PERCENT_COLUMN_CHART':
    case 'CHART.MULTI_MEASURE_GROUPED_BAR_CHART':
    case 'CHART.MULTI_MEASURE_STACKED_BAR_CHART':
    case 'CHART.MULTI_MEASURE_HUNDRED_PERCENT_BAR_CHART':
    case 'CHART.MULTI_MEASURE_MIRROR_BAR_CHART':
    case 'CHART.MULTI_MEASURE_MULTI_LINE_CHART':
    case 'CHART.MULTI_MEASURE_STACKED_AREA_CHART':
      return 'MEASURE';
    case 'CHART.SCATTER_PLOT_CHART':
      return 'SCATTER_Y';
    case 'CHART.REFERENCE_LINE_CHART':
    case 'CHART.STATIC_REFERENCE_LINE_CHART':
    case 'CHART.TREND_LINE_CHART':
      return undefined;
  }
  assertUnreachable(chartVisualizationKey);
}

/**
 * Checks if all bundle configurations are valid.
 *
 * @param attributeBundleDefinitions - bundle definitions
 * @param attributes - the DataView's attributes
 * @return - true if all bundle configurations are valid.
 */
export function hasValidBundlesConfig(
  attributeBundleDefinitions: AttributeBundleDefinitions,
  attributes: AttributesByBundle,
) {
  return Object.entries(getInvalidBundles(attributeBundleDefinitions, attributes)).length === 0;
}

/**
 * Gets the invalid bundles of the DataView.
 *
 * @param attributeBundleDefinitions - bundle definitions
 * @param attributes - the DataView's attributes
 * @return invalid bundles; key: bundle name,
 *   value: attribute count and limits
 */
function getInvalidBundles(
  attributeBundleDefinitions: AttributeBundleDefinitions,
  attributes: AttributesByBundle,
): ByBundle<BundleAttributeCountAndLimits> {
  const countAndLimitsPerBundle = getBundleAttributeCountAndLimits(attributeBundleDefinitions, attributes);
  return Object.fromEntries(
    Object.entries(countAndLimitsPerBundle).filter(([, bundleConfig]) => !isValidBundle(bundleConfig)),
  );
}

/**
 * Gets the attribute count and limits for the given bundles.
 *
 * @param attributeBundleDefinitions - bundle definitions
 * @param attributes - the DataView's attributes
 * @return attribute count and limits for the bundles
 */
export function getBundleAttributeCountAndLimits(
  attributeBundleDefinitions: AttributeBundleDefinitions,
  attributes: AttributesByBundle,
): ByBundle<BundleAttributeCountAndLimits> {
  return Object.fromEntries(
    Object.values(attributeBundleDefinitions).map((bundleDefinition) => [
      bundleDefinition.attributeBundleName,
      getAttributeCountAndLimits(bundleDefinition, attributes),
    ]),
  );
}

/**
 * Gets the attribute count and limits for a single bundle.
 *
 * @param attributeBundleDefinition - bundle definition
 * @param attributes - the DataView's attributes
 * @return attribute count and limits for the bundle
 */
function getAttributeCountAndLimits(
  attributeBundleDefinition: AttributeBundleDefinitionDto,
  attributes: AttributesByBundle,
): BundleAttributeCountAndLimits {
  return {
    current: attributes[attributeBundleDefinition.attributeBundleName]!.length,
    min: attributeBundleDefinition.minNumberOfAttributes ?? 0,
    max: attributeBundleDefinition.maxNumberOfAttributes ?? Number.POSITIVE_INFINITY,
  };
}

/**
 * Checks if the number of attributes in the bundle is within the allowed limits.
 *
 * @param bundleAttributeCountAndLimits - attribute count and limits for the bundle
 * @return whether the attribute count of the bundle is within the allowed limits
 */
export function isValidBundle(bundleAttributeCountAndLimits: BundleAttributeCountAndLimits) {
  return (
    bundleAttributeCountAndLimits.current >= bundleAttributeCountAndLimits.min &&
    bundleAttributeCountAndLimits.current <= bundleAttributeCountAndLimits.max
  );
}

/**
 * Collects all attribute ids from all bundles of the given data view.
 *
 */
export function getAllAttributeIdsFromDataView(dataView: DataViewDto) {
  return Object.values(dataView.attributes).flat();
}

export function getAllAttributes(dataViewId: DataViewId) {
  return Object.values(getAttributesByBundle(getStore().getState(), dataViewId)).flat();
}

export function findFirstAttributeWithStatus(dataViewId: DataViewId, attributeStatus: AttributeStatus[]) {
  return getAllAttributes(dataViewId).find((attribute) => attributeStatus.includes(attribute.status));
}

export function getBundleNameByAttributeId(dataViewId: DataViewId, attributeId: ActiveAttributeId) {
  const attributeBundles = getAttributesByBundle(getStore().getState(), dataViewId);
  const attributeBundleName = keys(attributeBundles).find((key) =>
    attributeBundles[key]!.some((attr) => attr.id === attributeId),
  );
  assertNonNullable(attributeBundleName);
  return attributeBundleName;
}

export function getBundleAttributes(dataViewId: DataViewId, attributeId: ActiveAttributeId) {
  const attributeBundleName = getBundleNameByAttributeId(dataViewId, attributeId);
  return requireNonNullable(
    getAttributesByBundle(getStore().getState(), dataViewId)[attributeBundleName],
    'An attribute bundle containing an attribute cannot be empty.',
  );
}

export function getDataViewByActiveAttributeId(attributeId: ActiveAttributeId) {
  const view = getActiveView(getStore().getState());
  assertNonNullable(view, 'there must be an active view to find a dataView by ActiveAttributeId');
  const dataViews = getDataViewsByViewId(getStore().getState(), view.id);
  return dataViews.find((el) => getAllAttributeIdsFromDataView(el).includes(attributeId));
}

export function modifyDataViewAttributeAndRules(
  dataViewId: DataViewId,
  attributeId: ActiveAttributeId,
  modifications: [AttributeModificationKey, string | null][],
  rules: AttributeHighlightingRule[],
) {
  return getStore()
    .dispatch(
      executeCommand(
        Command.COMPOSITE_COMMAND({
          commands: [
            ...createModificationCommands(dataViewId, attributeId, modifications),
            Command.SET_DATA_VIEW_ATTRIBUTE_HIGHLIGHTING_RULES({
              dataViewId,
              attributeId,
              attributeHighlightingRules: rules,
            }),
          ],
        }),
      ),
    )
    .unwrap();
}

export function getViewType(dataView: DataView) {
  return getViewTypeByDataViewId(getStore().getState(), dataView.dataViewId);
}

export function createModificationCommands(
  dataViewId: DataViewId,
  attributeId: ActiveAttributeId,
  modifications: [AttributeModificationKey, string | null][],
) {
  return modifications.map(([modification, value]) =>
    createDataViewAttributeModificationCommand(dataViewId, attributeId, modification, value),
  );
}

export function removePreconditionNotification(viewId: ViewId) {
  return getStore().dispatch(removeNotifications({ viewId, removedIds: [PRECONDITION_UNFULFILLED_NOTIFICATION_ID] }));
}

export function preconditionUnfulfilledNotification(viewId: ViewId, dataViewId: DataViewId) {
  getStore().dispatch(removeNotifications({ viewId, removedIds: [PRECONDITION_UNFULFILLED_NOTIFICATION_ID] }));
  return getStore().dispatch(
    addNotifications({
      viewId,
      added: [
        createNotification('INFO', 'VIEW', 'GENERIC', viewId, {
          notificationId: PRECONDITION_UNFULFILLED_NOTIFICATION_ID,
          title: getDisplayRuleHintText(dataViewId).hintText,
          skipDetail: true,
        }),
      ],
    }),
  );
}

export async function checkDataViewPreconditions(viewId: ViewId) {
  if (hasAnyValidDataViewWithPrecondition(viewId)) {
    const workbookNameAndHash = getWorkbookNameAndHash(getStore().getState());
    try {
      return await visiblePerPreconditions(workbookNameAndHash, viewId);
    } catch (error) {
      logger.error('Failed to check precondition', error);
    }
  }
  return { preconditionFulfilled: true };
}

function hasAnyValidDataViewWithPrecondition(viewId: ViewId) {
  const dataViewIds = getDataViewIdsByViewId(getStore().getState(), viewId);
  for (const dataViewId of dataViewIds) {
    const dataViewStatus = getDataViewStatus(getStore().getState(), dataViewId);
    const dataViewNotBroken = dataViewStatus === 'VALID' || dataViewStatus === 'INVALID_FROM_NOT_QUERY_RELEVANT_BUNDLE';
    if (dataViewNotBroken) {
      const preconditionBundle = getAttributesByBundle(getStore().getState(), dataViewId)['PRECONDITION'];
      if (preconditionBundle?.length && validatePreconditionBundle(dataViewId, preconditionBundle)) {
        return true;
      }
    }
  }
  return false;
}

export function validatePreconditionBundle(dataViewId: DataViewId, preconditionBundle: WorkbookAttributeDto[]) {
  const bundleDefinition = requireNonNullable(
    getDataViewById(getStore().getState(), dataViewId).attributeBundleDefinitions['PRECONDITION'],
  );
  const acceptedAttributeTypesByCategory = bundleDefinition.acceptedAttributeTypes;
  return preconditionBundle.every(
    (attribute) =>
      attribute.status === 'OK' &&
      bundleDefinition.acceptedAttributeCategories.includes(attribute.category) &&
      requireNonNullable(acceptedAttributeTypesByCategory[attribute.category]).includes(attribute.type) &&
      validatePreconditionAttributeModifications(attribute),
  );
}

function validatePreconditionAttributeModifications(attribute: WorkbookAttributeDto) {
  // Precondition attribute must have following modifications
  const aggregation = MeasureAggregation.fromPersistenceKey(
    requireNonNullable(getSetting<string>(attribute, AttributeModificationKey.MEASURE_AGGREGATION)),
  );
  const validAggregation = getAllowedAggregations(attribute).includes(aggregation);

  const value = requireNonNullable(getPreconditionModificationValue(attribute));
  let validValueFormat = true;

  if (conditionNeedsNumericValue(attribute.type, aggregation)) {
    const parsedNumber = parseNumber(value);
    const targetAggregationType = getTargetAttributeType(attribute.type, aggregation);
    validValueFormat = isConditionValueWholeNumber(parsedNumber, targetAggregationType);
  }

  return validAggregation && validValueFormat;
}

export function conditionNeedsNumericValue(attributeType: AttributeType, aggregation?: MeasureAggregation) {
  return isNumeric(aggregation ? getTargetAttributeType(attributeType, aggregation) : attributeType);
}

export function isConditionValueWholeNumber(value: number, attributeType: AttributeType) {
  return !(isNaN(value) || (conditionNeedsInteger(attributeType) && !Number.isInteger(value)));
}

function conditionNeedsInteger(type: AttributeType) {
  return type === 'INT' || type === 'LONG';
}

export function getPreconditionModificationValue(attribute: WorkbookAttributeDto) {
  const setting = getSetting<string | unknown>(attribute, AttributeModificationKey.PRECONDITION_VALUE);
  /*
   * To make settings usage convenient on client, we parse the settings somewhere to simplify access
   * for numbers, boolean - no need to treat them always as a string. In case of this modification, this is
   * a problem, We need to treat this value always as a string, so that we can identify when value
   * becomes invalid.
   */
  return setting != null ? String(setting) : undefined;
}

export function isDataViewComplete(dataViewId: DataViewId) {
  const dataView = getDataViewById(getStore().getState(), dataViewId);
  const attributesByBundle = getAttributesByBundle(getStore().getState(), dataView.id);
  return hasValidBundlesConfig(dataView.attributeBundleDefinitions, attributesByBundle);
}

export function getDataViewAcceptedAttributeIds(dataViewId: DataViewId) {
  const dataView = getDataViewById(getStore().getState(), dataViewId);
  if (!isValidMainObjecttype(dataView.mainObjecttype.objecttypeId)) {
    return getAttributes(getStore().getState()).flatMap((attribute) => attribute.id);
  }
  return getAcceptedAttributeIdsByDataViewId(getStore().getState(), dataViewId);
}

export function modifyDataViewAttribute(
  dataViewId: DataViewId,
  attributeId: ActiveAttributeId,
  modification: AttributeModificationKey | [AttributeModificationKey, string | null][],
  value: string | null = null,
) {
  if (Array.isArray(modification)) {
    // batch modification
    return getStore().dispatch(
      executeCommand(
        Command.COMPOSITE_COMMAND({
          commands: modification.map(([subModification, subValue]) =>
            createDataViewAttributeModificationCommand(dataViewId, attributeId, subModification, subValue),
          ),
        }),
      ),
    );
  } else {
    return getStore().dispatch(
      executeCommand(createDataViewAttributeModificationCommand(dataViewId, attributeId, modification, value)),
    );
  }
}
